import { useState, type FormEvent } from "react";
import { Download, FileText, Loader2, CheckCircle2 } from "lucide-react";
import type { GatedDownload as GatedDownloadType } from "@/content/posts/types";
import { useT } from "@/lib/i18n/I18nProvider";
import { toast } from "sonner";
import { track, trackFormFailure, trackFormSubmit } from "@/lib/analytics/track";
import { failureReasonForStatus } from "@/lib/analytics/cta-events";
import { logFormSubmission, hubspotFailReason } from "@/lib/form-log/client";

type Status = "idle" | "submitting" | "success" | "error";

export function GatedDownload({
  resource,
  postTitle,
}: {
  resource: GatedDownloadType;
  postTitle: string;
}) {
  const { locale } = useT();
  const isBg = locale === "bg";
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("submitting");
    setError(null);
    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: String(data.get("name") || ""),
      email: String(data.get("email") || ""),
      company: String(data.get("company") || ""),
      website: String(data.get("website") || ""),
      resourceSlug: resource.resourceSlug,
      resourceTitle: postTitle,
      pagePath: typeof window !== "undefined" ? window.location.pathname : "",
      formName: `gated-download:${resource.resourceSlug}`,
    };
    const backupBase = {
      formType: "gated-download",
      formName: payload.formName,
      pagePath: payload.pagePath,
      name: payload.name,
      email: payload.email,
      company: payload.company,
      message: `Requested resource: ${payload.resourceTitle}`,
      payload: { resourceSlug: payload.resourceSlug, locale: isBg ? "bg" : "en" },
    };
    const canBackup = /^\S+@\S+\.\S+$/.test(payload.email) && !payload.website;
    try {
      const useHubspotForms = resource.resourceSlug === "pr-crisis-management-checklist";
      let res: Response;
      let json: { ok?: boolean; error?: string } = {};
      if (useHubspotForms) {
        // Submit directly to the HubSpot Forms API so the form's automated
        // email and workflows are triggered. CORS is permitted by HubSpot.
        const [firstname, ...rest] = payload.name.trim().split(/\s+/);
        const lastname = rest.join(" ");
        res = await fetch(
          "https://api-eu1.hsforms.com/submissions/v3/integration/submit/25788344/9982ce9d-0f0b-448a-8c7a-a1c2b7f0f4e5",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              fields: [
                { objectTypeId: "0-1", name: "email", value: payload.email },
                { objectTypeId: "0-1", name: "firstname", value: firstname ?? "" },
                { objectTypeId: "0-1", name: "lastname", value: lastname },
                { objectTypeId: "0-1", name: "company", value: payload.company },
              ],
              context: {
                pageUri: typeof window !== "undefined" ? window.location.href : "",
                pageName: typeof document !== "undefined" ? document.title : "",
              },
              legalConsentOptions: {
                consent: {
                  consentToProcess: true,
                  text: "I agree to receive occasional emails from Perceptica.",
                },
              },
            }),
          },
        );
        json = res.ok ? { ok: true } : { ok: false };
      } else {
        res = await fetch("/api/public/gated-download", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        json = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      }
      if (!res.ok || !json.ok) {
        if (useHubspotForms && canBackup) void logFormSubmission({ ...backupBase, hubspotOk: false, hubspotReason: hubspotFailReason(res.status) });
        setStatus("error");
        const msg = json.error || (isBg ? "Нещо се обърка. Опитайте отново." : "Something went wrong. Please try again.");
        setError(msg);
        toast.error(msg);
        trackFormFailure({ form_name: payload.formName, reason: failureReasonForStatus(res.status), error_code: res.status, page_path: payload.pagePath, locale: isBg ? "bg" : "en" });
        return;
      }
      setStatus("success");
      if (useHubspotForms) void logFormSubmission({ ...backupBase, hubspotOk: true });
      toast.success(isBg ? "Готово - вижте имейла си за линка." : "Thanks - check your inbox for the download link.");
      trackFormSubmit("submit_gated_download", { form_name: payload.formName, page_path: payload.pagePath, locale: isBg ? "bg" : "en" });
    } catch {
      if (canBackup) void logFormSubmission({ ...backupBase, hubspotOk: false, hubspotReason: hubspotFailReason(null) });
      setStatus("error");
      const msg = isBg ? "Мрежова грешка. Опитайте отново." : "Network error. Please try again.";
      setError(msg);
      toast.error(msg);
      trackFormFailure({ form_name: payload.formName, reason: "network_error", page_path: payload.pagePath, locale: isBg ? "bg" : "en" });
    }
  }

  return (
    <section
      aria-labelledby="gated-download-heading"
      className="mt-16 overflow-hidden rounded-2xl border border-border/60 bg-gradient-to-br from-accent/5 via-background to-accent/10 p-6 lg:p-8"
    >
      <div className="flex items-start gap-4">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
          <FileText className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
            {isBg ? "Безплатен ресурс" : "Free download"}
          </p>
          <h2
            id="gated-download-heading"
            className="font-display mt-1 text-[clamp(1.4rem,2vw,1.75rem)] font-medium leading-tight text-foreground"
          >
            {resource.title}
          </h2>
          <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-muted-foreground">
            {resource.description}
          </p>
          {resource.fileLabel && (
            <p className="mt-1 text-[12px] text-muted-foreground">{resource.fileLabel}</p>
          )}
        </div>
      </div>

      {status === "success" ? (
        <div className="mt-6 rounded-xl border border-border/60 bg-background p-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden />
            <div className="min-w-0">
              <p className="text-[14px] font-medium text-foreground">{isBg ? "Благодарим - вашият файл е готов." : "Thanks - your download is ready."}</p>
              <p className="mt-1 text-[13px] text-foreground/70">
                {isBg ? "Изпратихме копие и на имейла ви." : "We've also emailed a copy to your inbox."}
              </p>
              <a
                href={resource.fileUrl}
                download={resource.fileName}
                className="mt-4 inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2.5 text-[13px] font-medium text-accent-foreground transition-opacity hover:opacity-90"
              >
                <Download className="h-4 w-4" aria-hidden />
                {isBg ? "Изтегли документа" : "Download the checklist"}
              </a>
            </div>
          </div>
        </div>
      ) : (
        <form name="gated-download" id="gated-download" data-hs-do-not-collect="true" onSubmit={handleSubmit} className="mt-6 grid gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-1">
            <span className="text-[12px] font-medium text-foreground/70">{isBg ? "Име и фамилия" : "Full name"}</span>
            <input
              required
              name="name"
              type="text"
              autoComplete="name"
              className="mt-1.5 w-full rounded-lg border border-border/60 bg-background px-3 py-2.5 text-[14px] text-foreground outline-none transition-colors focus:border-accent"
              placeholder={isBg ? "Иван Иванов" : "Jane Doe"}
            />
          </label>
          <label className="block sm:col-span-1">
            <span className="text-[12px] font-medium text-foreground/70">{isBg ? "Служебен имейл" : "Work email"}</span>
            <input
              required
              name="email"
              type="email"
              autoComplete="email"
              className="mt-1.5 w-full rounded-lg border border-border/60 bg-background px-3 py-2.5 text-[14px] text-foreground outline-none transition-colors focus:border-accent"
              placeholder={isBg ? "ivan@company.bg" : "jane@company.com"}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="text-[12px] font-medium text-foreground/70">
              {isBg ? "Компания" : "Company"} <span className="text-muted-foreground">{isBg ? "(по желание)" : "(optional)"}</span>
            </span>
            <input
              name="company"
              type="text"
              autoComplete="organization"
              className="mt-1.5 w-full rounded-lg border border-border/60 bg-background px-3 py-2.5 text-[14px] text-foreground outline-none transition-colors focus:border-accent"
              placeholder={isBg ? "Вашата компания" : "Acme Inc."}
            />
          </label>
          {/* honeypot */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="hidden"
          />
          <div className="sm:col-span-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-[12px] text-muted-foreground">
              {isBg
                ? "С изтеглянето се съгласявате да получавате периодични имейли от Perceptica. Можете да се отпишете по всяко време."
                : "By downloading you agree to receive occasional emails from Perceptica. Unsubscribe anytime."}
            </p>
            <button
              type="submit"
              disabled={status === "submitting"}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-[13px] font-medium text-accent-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {status === "submitting" ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Download className="h-4 w-4" aria-hidden />
              )}
              {status === "submitting"
                ? isBg ? "Изпраща..." : "Sending..."
                : isBg ? "Изтегли документа" : "Get the checklist"}
            </button>
          </div>
          {error && (
            <p className="sm:col-span-2 text-[13px] text-destructive" role="alert">
              {error}
            </p>
          )}
        </form>
      )}
    </section>
  );
}